/**
 * useSolutionMapMutations — centralized write-side hook for the SolutionMap
 * edit mode (Industry view). Covers all 9 mutation actions in design §3.4 +
 * routes them through per-industry locks with the matching optimistic /
 * re-fetch strategy.
 *
 * Why this lives here (not in a topicClient-style facade):
 *   - All mutations are page-local (SolutionMapPage only)
 *   - The lock state IS UI state — disabling buttons during in-flight writes
 *   - The cell / row components don't need to know the SDK exists
 *
 * The hook takes a `reloadIndustry(industry)` callback so the page keeps
 * ownership of the `maps: Map<industry, GetSolutionMapOutput>` cache and can
 * patch / re-fetch in a single source of truth.
 */

import { useCallback, useState } from 'react';
import {
  DeprecateSolutionMapAreaCommand,
  getIvtClient,
  RenameSolutionMapAreaCommand,
  RenameSolutionMapUcCommand,
  ReorderAction,
  ReorderDirection,
  ReorderSolutionMapCommand,
  UpdateSolutionMapCommand,
  type ReorderSolutionMapCommandInput,
  type SolutionMapEntry,
  type SolutionStatus,
} from '../../../client/IvtHubClient';
import { denialHint } from '../../../utils/authorization';
import { SPAN_AREA } from '../utils/spanCoverage';

export interface UseSolutionMapMutationsParams {
  /** Called after a successful mutation that needs a fresh per-industry pull. */
  reloadIndustry: (industry: string) => Promise<void>;
  /**
   * Apply an entry returned by UpdateSolutionMap into the page's maps cache,
   * for the optimistic-then-patch path. Implementation should locate the entry
   * by `(industry, entry.id)` and replace it.
   */
  patchEntry: (industry: string, entry: SolutionMapEntry) => void;
}

interface State {
  locked: Set<string>;       // industries with in-flight writes
  error: string | null;
}

interface UpsertArgs {
  industry: string;
  solutionArea: string;
  useCase: string;
  status?: SolutionStatus;
  priority?: number;
  notes?: string;
  assetId?: string;
}

/**
 * What the caller is trying to create, for `createEntry`'s 409 copy only.
 *
 * All three intents are the same UpdateSolutionMap write tripping the same unique index, but
 * the way OUT differs and the payload cannot reveal which. Adding a column is an ordinary UC
 * write carrying a placeholder name, so without this the user is told to model a cross-area
 * UC when all they wanted was a column, and the one-keystroke fix (rename the placeholder) is
 * never named.
 *
 * REQUIRED, deliberately, with no default. A default is silently wrong at whichever call site
 * forgets it, and that is not testable per site — measured: dropping the argument at one of
 * the span call sites left all 20 tests green. Required makes each omission a compile error
 * instead (tsc covers the page; it does NOT cover `*.test.ts`).
 */
type CreateIntent = 'uc' | 'column' | 'span';

export function useSolutionMapMutations({
  reloadIndustry,
  patchEntry,
}: UseSolutionMapMutationsParams) {
  const [state, setState] = useState<State>({ locked: new Set(), error: null });

  const isLocked = useCallback(
    (industry: string) => state.locked.has(industry),
    [state.locked],
  );

  const dismissError = useCallback(() => {
    setState((s) => (s.error === null ? s : { ...s, error: null }));
  }, []);

  /**
   * Run an async block under the industry's lock; runs ONE optimistic patch up
   * front (if provided), then calls `body`. On success, optionally `onSuccess`
   * with the SDK response (typical use: `patchEntry`); on error, runs
   * `rollback`, surfaces the message via `state.error`, and lets the caller
   * inspect the SDK error name via `onError` (so e.g. reorder can force a
   * re-fetch on 404).
   *
   * Returns `null` on success, otherwise the user-facing failure message.
   *
   * NOT a boolean: every mutation here is now write-gated, and the modals that host
   * these actions used to print their own `保存失败，请重试` on a falsy result — wrong
   * advice for a 403, and it hid the message this hook had already computed. Handing
   * the message back is what lets one classification serve both the page-level Alert
   * and the open modal.
   */
  const runUnderLock = useCallback(
    async <T,>(
      industry: string,
      body: () => Promise<T>,
      opts?: {
        optimistic?: () => void;
        onSuccess?: (out: T) => void | Promise<void>;
        rollback?: () => void;
        /** Lets the caller customize behaviour by Smithy error name. Return
         *  a string to replace the default `state.error` message; return
         *  void to keep the default. The reload after this resolves. */
        onError?: (err: Error) => void | string | Promise<void | string>;
      },
    ): Promise<string | null> => {
      let blocked = false;
      setState((s) => {
        if (s.locked.has(industry)) {
          blocked = true;
          return s;
        }
        const locked = new Set(s.locked);
        locked.add(industry);
        return { ...s, locked, error: null };
      });
      if (blocked) {
        opts?.rollback?.();
        // Already-locked is a UI guard, not a failure the user should be told about —
        // the button that triggered it is disabled while the lock is held.
        return null;
      }

      opts?.optimistic?.();
      try {
        let out: T;
        try {
          out = await body();
        } catch (e) {
          opts?.rollback?.();
          const err = e as Error;
          const override = opts?.onError ? await opts.onError(err) : undefined;
        // Non-denial fallback leads with the ACTION and keeps the server's text as a
        // parenthetical diagnostic. Returning `err.message` alone (as this did) put raw
        // English SDK text ("Failed to fetch") in the modals, where mainline showed
        // 保存失败，请重试 — and it contradicts the convention this change uses everywhere
        // else (`denialHint(e) ?? '…请重试。'`). `err.message` is never empty for a
        // service error: decorateServiceException forces it to at least 'UnknownError'.
          const message =
            (typeof override === 'string' && override) ||
            denialHint(e) ||
            (err.message ? `操作失败，请重试。（${err.message}）` : '操作失败，请重试。');
          setState((s) => ({ ...s, error: message }));
          return message;
        }

        // Past this point the WRITE HAS COMMITTED, so nothing below may be reported as a
        // failure of the action. `onSuccess` is where every caller does its post-write
        // reload, and a reload can reject on its own (network, or a 403 once the session
        // expired) — letting that fall into the catch above told the user their write
        // failed when it had landed, kept the host modal open, and re-enabled Save for a
        // pointless re-run. Resolve through the SUCCESS channel and put the stale-view
        // notice at page level instead.
        if (opts?.onSuccess) {
          try {
            await opts.onSuccess(out);
          } catch {
            setState((s) => ({
              ...s,
              error: '操作已完成，但刷新视图失败，请重新加载页面查看最新状态。',
            }));
          }
        }
        return null;
      } finally {
        setState((s) => {
          if (!s.locked.has(industry)) return s;
          const locked = new Set(s.locked);
          locked.delete(industry);
          return { ...s, locked };
        });
      }
    },
    [],
  );

  // Smithy errors expose a `name` field (e.g. "ResourceNotFoundException").
  // The wire-level service exception body also stamps `__type`, but the
  // generated client copies it into `.name`, so this is the contract.
  const errorName = (err: Error): string | undefined =>
    (err as Error & { name?: string }).name;

  // ── UPDATE an EXISTING cell (priority / status / notes) ───────────────────
  // Strategy: caller does the optimistic patch BEFORE calling this (so the UI turns
  // instantly); on success we re-patch with the canonical entry from the response; on
  // failure we run the caller-provided `rollback`.
  //
  // `solutionMapId` is REQUIRED here, deliberately — this function only ever edits a row
  // the user is looking at, so it always has one, and requiring it is what makes a
  // name-addressed edit unreachable from this app. A name is not a stable identifier:
  // delete a UC and re-create it under the same name and you get a NEW row with a NEW id,
  // so a name read earlier can address a row the user never saw — and get a 200 for
  // editing or DELETING it.
  //
  // Only `status` / `priority` / `notes` / `assetId` are written on the id path.
  // `solutionArea` / `useCase` still go on the wire (both @required on the model) but are
  // neither used for addressing nor applied as edits: this op cannot rename or move a row.
  // Renaming is `renameUc`; moving between columns has no op at all.

  const updateEntry = useCallback(
    (args: UpsertArgs & { solutionMapId: number }, rollback?: () => void) =>
      runUnderLock(
        args.industry,
        () =>
          getIvtClient().send(
            new UpdateSolutionMapCommand({
              industry: args.industry,
              solutionArea: args.solutionArea,
              useCase: args.useCase,
              status: args.status,
              priority: args.priority,
              notes: args.notes,
              assetId: args.assetId,
              solutionMapId: args.solutionMapId,
            }),
          ),
        {
          onSuccess: (out) => {
            if (out.entry) patchEntry(args.industry, out.entry);
          },
          rollback,
          // With an id the tombstone rules collapse to one: no LIVE row with that id is a
          // 404, full stop. (The old "status omitted counts as an edit" carve-out existed
          // only because the name path could not tell create from edit.) Reachable from a
          // stale second tab still showing a deleted UC — and a reload is exactly what such
          // a tab needs, which a generic failure would not give it.
          //
          // A repeated DELETE of the same id is NOT this case: the op is @idempotent, so it
          // answers 200 with the row (status DEPRECATED). Double-clicking Delete, or an SDK
          // retry after a lost response, is therefore safe for correctness — debouncing it
          // would only be a polish concern.
          onError: async (err) => {
            if (errorName(err) === 'ResourceNotFoundException') {
              await reloadIndustry(args.industry).catch(() => undefined);
              return '该 UC 已被删除，已重新加载';
            }
          },
        },
      ),
    [patchEntry, reloadIndustry, runUnderLock],
  );

  // ── EDIT a cross-area UC's covered-column list ────────────────────────────
  // Same op as `updateEntry`, but split out for two reasons rather than folded in:
  //
  //   1. It used to run through `createEntry` — addressed by (industry, '_span_', useCase),
  //      i.e. by NAME, which is exactly the hazard the id closes. It is an EDIT of an
  //      existing row and now says so.
  //   2. It RELOADS instead of patching. A patch would work only if the upsert RESPONSE
  //      echoes `notes` back, and that is a different code path from the read the page
  //      already trusts — while `notes` is the single field deciding this row's rendered
  //      start/end columns, and the covered names are order-sensitive. Reload keeps the
  //      pre-existing behaviour exactly and depends on one fewer thing. (Do NOT justify
  //      this with "the SDK strips notes" — it does not; `notes` is declared on the
  //      generated SolutionMapEntry, which is why the raw-fetch bypass was deleted.)

  const updateSpanCoverage = useCallback(
    (input: { industry: string; solutionMapId: number; useCase: string; notes: string }) =>
      runUnderLock(
        input.industry,
        () =>
          getIvtClient().send(
            new UpdateSolutionMapCommand({
              industry: input.industry,
              // Compat-only on the id path, and never applied as an edit — so the sentinel
              // here is describing the row, not moving anything to it.
              solutionArea: SPAN_AREA,
              useCase: input.useCase,
              notes: input.notes,
              solutionMapId: input.solutionMapId,
            }),
          ),
        {
          onSuccess: () => reloadIndustry(input.industry),
          onError: async (err) => {
            if (errorName(err) === 'ResourceNotFoundException') {
              await reloadIndustry(input.industry).catch(() => undefined);
              return '该跨列 UC 已被删除，已重新加载';
            }
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  // ── CREATE NEW UC / AREA / SPAN UC (all flow through UpdateSolutionMap) ───
  // Strategy: reload the whole industry — backend assigns area_order / uc_order.
  // No `solutionMapId`: the row does not exist yet, so the business key is the only way to
  // address it. That is the real reason the field is optional on the model.

  const createEntry = useCallback(
    (args: UpsertArgs, intent: CreateIntent) =>
      runUnderLock(
        args.industry,
        () =>
          getIvtClient().send(
            new UpdateSolutionMapCommand({
              industry: args.industry,
              solutionArea: args.solutionArea,
              useCase: args.useCase,
              status: args.status ?? 'ACTIVE',
              priority: args.priority ?? 0,
              notes: args.notes,
              assetId: args.assetId,
            }),
          ),
        {
          onSuccess: () => reloadIndustry(args.industry),
          // V69 made a UC name unique per INDUSTRY, not per column, so creating one under
          // a name a different live column already uses is now a 409. The default
          // 操作失败，请重试 would be actively wrong here — retrying cannot help, and the
          // refusal reads as arbitrary unless the copy names the alternative. Cross-INDUSTRY
          // reuse is still allowed ("Digital Customer Experience" is live in five).
          onError: (err) => {
            if (errorName(err) !== 'ConflictException') return;
            // One index, three remedies. `请重试` is never one of them: a unique-index
            // violation is not transient.
            //
            // The server's ConflictException message DOES name the column holding the name
            // (SolutionMapWriteDao.forUcNameInIndustry). It is not surfaced because the only
            // member ConflictException declares is a free-prose English `message`, so getting
            // the column out means regex-ing server prose — and with the name unique per
            // industry there is exactly one holder, visible in the matrix on screen.
            if (intent === 'column') {
              // The AREA name is not the problem, and saying "已被占用" without saying which
              // name would send the user off renaming the column. NewAreaModal pre-fills the
              // placeholder UC name, so creating two columns and accepting that default
              // twice is the likeliest way this 409 ever fires.
              return `占位 UC 名「${args.useCase}」在本 industry 已被占用（UC 名跨列唯一，与列名无关）。把占位 UC 改个名字即可创建这一列。`;
            }
            if (intent === 'span') {
              // Holder can only be a real column's UC: a same-named `_span_` row would have
              // matched this very upsert's key and been UPDATED, not refused. And "model it
              // as a cross-area UC" is not advice for someone already creating one.
              return `UC 名「${args.useCase}」已被本 industry 某一列里的 UC 占用。一个 UC 不能同时是跨列 UC 和列内 UC，请先删除或改名那个 UC。`;
            }
            return `UC 名「${args.useCase}」在本 industry 已被占用（UC 名跨列唯一）。改个名字，或者若你要让它覆盖多个列，请改用跨列 UC。`;
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  // ── REORDER ───────────────────────────────────────────────────────────────
  // Split into two functions on purpose. One `reorder({action, target, solutionMapId?})`
  // typed the id as optional — it has to be, because MOVE_AREA genuinely has none: a column
  // is not an entity, it is just the name its rows share. But that made the id optional for
  // MOVE_UC too, so a second UC-reorder caller (keyboard reorder, a bulk action) could omit
  // it, compile clean, keep every test green, and silently fall back to name addressing.
  // Two functions let MOVE_UC require the id while MOVE_AREA never asks for one.
  //
  // Response carries no order data → reload on success. On 404 the local view is stale
  // (server-side boundary check, or the target moved out from under us), so re-fetch even on
  // failure to converge.

  const reorderCommon = useCallback(
    (industry: string, input: ReorderSolutionMapCommandInput) =>
      runUnderLock(
        industry,
        () => getIvtClient().send(new ReorderSolutionMapCommand(input)),
        {
          onSuccess: () => reloadIndustry(industry),
          onError: async (err) => {
            if (errorName(err) === 'ResourceNotFoundException') {
              await reloadIndustry(industry).catch(() => undefined);
              return '记录不存在或已被移动，已重新加载';
            }
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  /** Move a UC within its column. Addressed by id; `target` rides along because the model
   *  still requires it, and the server prefers the id. */
  const reorderUc = useCallback(
    (input: {
      industry: string;
      solutionMapId: number;
      solutionArea: string;
      useCase: string;
      direction: ReorderDirection;
    }) =>
      reorderCommon(input.industry, {
        industry: input.industry,
        action: ReorderAction.MOVE_UC,
        // "<area>||<useCase>", split on a separator a UC name is not forbidden from
        // containing — the second reason the id wins server-side.
        target: `${input.solutionArea}||${input.useCase}`,
        solutionMapId: input.solutionMapId,
        direction: input.direction,
      }),
    [reorderCommon],
  );

  /** Move a whole column left/right. No id exists for a column. */
  const reorderArea = useCallback(
    (input: { industry: string; area: string; direction: ReorderDirection }) =>
      reorderCommon(input.industry, {
        industry: input.industry,
        action: ReorderAction.MOVE_AREA,
        target: input.area,
        direction: input.direction,
      }),
    [reorderCommon],
  );

  // ── RENAME UC (incl. span row, solutionArea='_span_') ─────────────────────
  // Strategy: optimistic patch use_case, then re-fetch (other rows may reference the
  // old name). Addressed by `solutionMapId`, which is REQUIRED here on purpose:
  //
  // A UC name stopped being a stable identifier. Deleting a UC and re-creating it under
  // the same name now yields a NEW row with a NEW id (it used to revive the original
  // row, so a name mapped to one row for life). A client that renames by the name it
  // read earlier can therefore rewrite a row the user never saw — and get a 200. The id
  // turns that into a 404, which is what the reload branch below is for. Leaving the
  // name path reachable from this app would just keep that hole open.

  const renameUc = useCallback(
    (input: {
      industry: string;
      solutionArea: string;
      oldName: string;
      newName: string;
      solutionMapId: number;
      optimisticPatch?: () => void;
      rollback?: () => void;
    }) =>
      runUnderLock(
        input.industry,
        () =>
          getIvtClient().send(
            new RenameSolutionMapUcCommand({
              industry: input.industry,
              // Ignored server-side once solutionMapId is present, but both are @required
              // on the model, so they still have to go on the wire.
              solutionArea: input.solutionArea,
              oldName: input.oldName,
              newName: input.newName,
              solutionMapId: input.solutionMapId,
            }),
          ),
        {
          optimistic: input.optimisticPatch,
          rollback: input.rollback,
          onSuccess: () => reloadIndustry(input.industry),
          onError: async (err) => {
            const name = errorName(err);
            // V69: unique per INDUSTRY, not per column, so the collision is no longer
            // necessarily in the column the user is looking at — say where it is.
            if (name === 'ConflictException') {
              return `新名字「${input.newName}」在本 industry 已被占用（UC 名跨列唯一）`;
            }
            // Addressed by id, so this is not "moved" any more — the row is gone.
            if (name === 'ResourceNotFoundException') {
              await reloadIndustry(input.industry).catch(() => undefined);
              return '该 UC 已被删除，已重新加载';
            }
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  // ── DELETE Area: ONE atomic backend op ────────────────────────────────────
  // Was N serial UpdateSolutionMap(DEPRECATED) writes, one per UC, because no op existed.
  // Two defects came with that, and `DeprecateSolutionMapArea` (CR-302148852) closes both
  // in one transaction:
  //
  //   1. NOT ATOMIC. A failure part-way left the column half deleted, and all this hook
  //      could do was tell the user how far it got and ask them to reload and look.
  //   2. SPAN COVERAGE WENT STALE. A cross-area UC stores the columns it covers as a list
  //      of column NAMES in `notes`. The loop never touched those, so deleting a column
  //      left its name in the list — and `computeSpanRange`'s fallback is silent: no
  //      stored name resolves → renders as "covers every column"; some resolve → the
  //      range quietly shrinks. The op rewrites the lists in the same transaction.
  //
  // No `ucNames`: the backend finds the column's live rows itself. That also retires the
  // trap that the caller had to pass ACTIVE names only — sending DEPRECATED to a name that
  // was already a tombstone 404'd and aborted the loop. The read has since stopped returning
  // tombstones (CR-302136400), so a caller could no longer pass one by accident, but the
  // enumeration is gone either way: the column is the unit, not a list of names.

  const deprecateArea = useCallback(
    (input: { industry: string; area: string }) =>
      runUnderLock(
        input.industry,
        () =>
          getIvtClient().send(
            new DeprecateSolutionMapAreaCommand({
              industry: input.industry,
              area: input.area,
            }),
          ),
        {
          onSuccess: () => reloadIndustry(input.industry),
          onError: async (err) => {
            // 404 here means ZERO rows of ANY status under (industry, area) — the server
            // counts status-blind precisely so it can tell this apart from an already-deleted
            // column, which has tombstones and answers 200 with deprecatedCount 0
            // (@idempotent). So "已被删除" would name the one cause this branch CANNOT have.
            // What it can be: the column was renamed under a stale tab, or the name never
            // existed. `_span_` reaches the same 404, but no column header renders for it.
            if (errorName(err) === 'ResourceNotFoundException') {
              await reloadIndustry(input.industry).catch(() => undefined);
              return '该列不存在（可能已被改名），已重新加载';
            }
            // Everything else falls through to runUnderLock's denial classification /
            // generic message, same as every other mutation here.
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  // ── RENAME Area: ONE atomic backend op ────────────────────────────────────
  // Was a two-loop simulation (insert every UC under the new name, then deprecate the old
  // ones) because no op existed. That rewrote far more than the column name: new
  // `solution_map.id` per UC — orphaning every `uc_asset_link` — plus status forced to
  // ACTIVE, priority zeroed, notes cleared, `area_order` recomputed (the column jumped to
  // the far right), a tombstone left per UC, and no atomicity. `RenameSolutionMapArea`
  // (CR-302054976) does it as one transaction that keeps every id, so links survive, the
  // column keeps its position, and priority/notes/status are untouched. It also rewrites
  // the covered-area names inside span rows' `notes`, which the loop never did.
  //
  // No `ucs` argument: the backend finds the column's live rows itself. And no half-applied
  // warning — with one transaction that intermediate state cannot exist.

  const renameArea = useCallback(
    (input: { industry: string; oldArea: string; newArea: string }) =>
      runUnderLock(
        input.industry,
        () =>
          getIvtClient().send(
            new RenameSolutionMapAreaCommand({
              industry: input.industry,
              oldArea: input.oldArea,
              newArea: input.newArea,
            }),
          ),
        {
          onSuccess: () => reloadIndustry(input.industry),
          onError: async (err) => {
            const name = errorName(err);
            // 409 guards against silently merging two columns. Only LIVE rows collide —
            // a name whose UCs are all deprecated is reusable and answers 200.
            if (name === 'ConflictException') {
              return `列名「${input.newArea}」已被占用（该列还有未删除的 UC）`;
            }
            // 404 = the source column has no live rows (already renamed, or a stale view).
            // Also returned when either side is the `_span_` sentinel, which is not a column.
            if (name === 'ResourceNotFoundException') {
              await reloadIndustry(input.industry).catch(() => undefined);
              return '原列不存在或已被改名，已重新加载';
            }
          },
        },
      ),
    [reloadIndustry, runUnderLock],
  );

  return {
    isLocked,
    error: state.error,
    dismissError,
    updateEntry,
    updateSpanCoverage,
    createEntry,
    reorderUc,
    reorderArea,
    renameUc,
    deprecateArea,
    renameArea,
  };
}
